import React, { useState } from "react";
import { Alert, Box, Button, Card, CardContent, Checkbox, FormControlLabel, Grid, TextField, Typography } from "@mui/material";
import { bank } from "./api";
import { Evidence, OutcomeLog, outcome, PhonePrompt, usePaymentStatus } from "./components";

/**
 * Bank side of transaction signing:
 *  1 the bank creates the payment -> push to the user's phone
 *  2 the user approves or rejects on the phone (Approver page or the mobile SDK)
 *  3 the bank executes: the executed details must match the approved ones (tamper check)
 *  4 executing twice is refused (replay)
 */
export default function PaymentDemo({ username }) {
  const [form, setForm] = useState({
    amount: "250.00",
    currency: "AUD",
    beneficiaryName: "Test Payee",
    beneficiaryAccount: "12345678",
    beneficiaryBankCode: "062000",
    reference: "Demo payment",
    simulatePushFailure: false,
  });
  const [created, setCreated] = useState(null);
  const [final, setFinal] = useState(null);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);

  const add = (entry) => setLog((previous) => [entry, ...previous]);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const status = usePaymentStatus(created?.requestId, (done) => {
    setFinal(done);
    add({
      label: `Phone decision: ${done.status}${done.rejectReason ? ` (${done.rejectReason})` : ""}`,
      ok: done.status === "APPROVED",
      blocked: done.status !== "APPROVED",
    });
  });

  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  const create = () =>
    run(async () => {
      setCreated(null);
      setFinal(null);
      const res = await bank.post("/payments", { username, ...form });
      add(outcome(`Create payment ${form.currency} ${form.amount} to ${form.beneficiaryName}`, res));
      if (res.status === 201) setCreated(res.data);
    });

  const execute = (tamper) =>
    run(async () => {
      const res = await bank.post(`/payments/${created.requestId}/execute`, { tamper });
      const changed = res.data?.details?.changedFields?.join(", ");
      const label =
        tamper === "none"
          ? "Bank executes the approved details"
          : `Bank executes with tampered ${tamper}${changed ? ` (changed: ${changed})` : ""}`;
      add(outcome(label, res, tamper !== "none" || res.data?.error === "TXN_ALREADY_USED_OR_EXPIRED"));
    });

  const cancel = () =>
    run(async () => {
      const res = await bank.post(`/payments/${created.requestId}/cancel`);
      add(outcome("Bank cancels the payment", res));
    });

  const approverUrl = created ? `${window.location.origin}/security-demo/approver?requestId=${created.requestId}` : "";
  const open = created && (!status || ["PENDING", "CHALLENGE_ISSUED"].includes(status.status));

  return (
    <Box>
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6">1. Bank creates a payment</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The bank backend freezes the details and Passwordless4u sends a push to {username || "the user"}'s phone. The
            push carries only the request id.
          </Typography>
          <Grid container spacing={2}>
            {[
              ["amount", "Amount"],
              ["currency", "Currency"],
              ["beneficiaryName", "Beneficiary name"],
              ["beneficiaryAccount", "Beneficiary account"],
              ["beneficiaryBankCode", "Bank code"],
              ["reference", "Reference"],
            ].map(([key, label]) => (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <TextField fullWidth size="small" label={label} value={form[key]} onChange={set(key)} />
              </Grid>
            ))}
          </Grid>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", mt: 2, flexWrap: "wrap" }}>
            <FormControlLabel
              control={<Checkbox checked={form.simulatePushFailure} onChange={set("simulatePushFailure")} />}
              label="Simulate push failure"
            />
            <Button variant="contained" onClick={create} disabled={busy || !username}>
              Create payment
            </Button>
            {open && (
              <Button onClick={cancel} disabled={busy}>
                Cancel
              </Button>
            )}
          </Box>
          {!username && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Enter the demo user's email above.
            </Alert>
          )}
        </CardContent>
      </Card>

      {created && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6">2. User approves or rejects on the phone</Typography>
            <Evidence
              rows={[
                ["Request", created.requestId],
                ["Transaction hash (txHash)", created.txHash],
                ["Canonical version", created.canonicalVersion],
              ]}
            />
            <PhonePrompt expiresAt={created.expiresAt} delivery={created.delivery} status={status?.status} />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Phone stand-in (open on a device with the user's passkey):{" "}
              <a href={approverUrl} target="_blank" rel="noreferrer">
                {approverUrl}
              </a>
            </Typography>
          </CardContent>
        </Card>
      )}

      {final?.status === "APPROVED" && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6">3. Bank executes the payment</Typography>
            <Evidence
              rows={[
                ["Approved by", final.approvedBy?.deviceName || final.approvedBy?.deviceType],
                ["User verified", String(final.approvedBy?.userVerified)],
              ]}
            />
            <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>
              Try executing with changed details first: each is refused with TXN_TAMPERED and the approval stays usable.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button variant="outlined" color="error" onClick={() => execute("amount")} disabled={busy}>
                Tamper amount
              </Button>
              <Button variant="outlined" color="error" onClick={() => execute("account")} disabled={busy}>
                Tamper account
              </Button>
              <Button variant="outlined" color="error" onClick={() => execute("name")} disabled={busy}>
                Tamper name
              </Button>
              <Button variant="contained" color="success" onClick={() => execute("none")} disabled={busy}>
                No change: execute
              </Button>
            </Box>
            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              4. Replay: execute again
            </Typography>
            <Button variant="outlined" onClick={() => execute("none")} disabled={busy}>
              Execute the same payment again
            </Button>
          </CardContent>
        </Card>
      )}

      <OutcomeLog entries={log} />
    </Box>
  );
}
